import { BillingCategory, Category } from './index';

/**
 * WhatsApp Business API pricing per message (in INR).
 * Based on Meta's INR rate card effective October 1, 2026
 * Source: developers.facebook.com/documentation/business-messaging/whatsapp/pricing ("INR rates" CSV/PDF)
 * Each row carries the card's Marketing, Utility, Authentication and Service
 * columns (Service = free-form replies, charged from 2026-10-01).
 *
 * Every rate lives exactly once: explicitly priced markets in EXPLICIT_PRICING,
 * the regional rows in WHATSAPP_REGION_PRICING, and which countries a region
 * covers in REGION_COUNTRIES. WHATSAPP_PRICING (per ISO code) is derived.
 */

export type CategoryPricing = {
  [Category.MARKETING]: number;
  [Category.UTILITY]: number;
  [Category.AUTHENTICATION]: number;
  [BillingCategory.SERVICE]: number;
};

// Partial pricing for custom overrides (not all categories required)
export type PartialCategoryPricing = Partial<CategoryPricing>;

// Custom pricing overrides by country ISO code
// Example: { "IN": { "MARKETING": 0.75 }, "US": { "MARKETING": 1.00, "UTILITY": 0.50 } }
export type CustomPricing = Record<string, PartialCategoryPricing>;

export type WhatsAppPricing = Record<string, CategoryPricing>;

/** Markets Meta prices individually (rate-card rows), ISO-2 → INR per message. */
export const EXPLICIT_PRICING: Record<string, CategoryPricing> = {
  // Argentina
  AR: {
    MARKETING: 4.5293,
    UTILITY: 1.9062,
    AUTHENTICATION: 1.9062,
    SERVICE: 1.9062,
  },
  // Bangladesh
  BD: {
    MARKETING: 5.3664,
    UTILITY: 0.2712,
    AUTHENTICATION: 0.2712,
    SERVICE: 0.2712,
  },
  // Brazil
  BR: {
    MARKETING: 4.5808,
    UTILITY: 0.4984,
    AUTHENTICATION: 0.4984,
    SERVICE: 0.4984,
  },
  // Chile
  CL: {
    MARKETING: 6.5135,
    UTILITY: 1.4654,
    AUTHENTICATION: 1.4654,
    SERVICE: 1.4654,
  },
  // Colombia
  CO: {
    MARKETING: 0.9161,
    UTILITY: 0.0588,
    AUTHENTICATION: 0.0588,
    SERVICE: 0.0588,
  },
  // Egypt
  EG: {
    MARKETING: 4.7191,
    UTILITY: 0.2639,
    AUTHENTICATION: 0.2639,
    SERVICE: 0.2639,
  },
  // France
  FR: {
    MARKETING: 6.299,
    UTILITY: 2.1994,
    AUTHENTICATION: 2.1994,
    SERVICE: 2.1994,
  },
  // Germany
  DE: {
    MARKETING: 10.0073,
    UTILITY: 4.0322,
    AUTHENTICATION: 4.0322,
    SERVICE: 4.0322,
  },
  // Hong Kong
  HK: {
    MARKETING: 5.3664,
    UTILITY: 1.9056,
    AUTHENTICATION: 1.9056,
    SERVICE: 1.9056,
  },
  // Hungary
  HU: {
    MARKETING: 6.2997,
    UTILITY: 2.5583,
    AUTHENTICATION: 2.5583,
    SERVICE: 2.5583,
  },
  // India
  IN: {
    MARKETING: 0.8631,
    UTILITY: 0.115,
    AUTHENTICATION: 0.115,
    SERVICE: 0.115,
  },
  // Indonesia
  ID: {
    MARKETING: 3.0111,
    UTILITY: 1.8316,
    AUTHENTICATION: 1.8316,
    SERVICE: 1.8316,
  },
  // Iraq
  IQ: {
    MARKETING: 2.4993,
    UTILITY: 0.5786,
    AUTHENTICATION: 0.5786,
    SERVICE: 0.5786,
  },
  // Israel
  IL: {
    MARKETING: 2.5871,
    UTILITY: 0.3885,
    AUTHENTICATION: 0.3885,
    SERVICE: 0.3885,
  },
  // Italy
  IT: {
    MARKETING: 5.8206,
    UTILITY: 2.1974,
    AUTHENTICATION: 2.1974,
    SERVICE: 2.1974,
  },
  // Kazakhstan
  KZ: {
    MARKETING: 4.4248,
    UTILITY: 1.3187,
    AUTHENTICATION: 1.3187,
    SERVICE: 1.3187,
  },
  // Kuwait
  KW: {
    MARKETING: 5.8059,
    UTILITY: 3.2226,
    AUTHENTICATION: 3.2226,
    SERVICE: 3.2226,
  },
  // Malaysia
  MY: {
    MARKETING: 6.3037,
    UTILITY: 1.0262,
    AUTHENTICATION: 1.0262,
    SERVICE: 1.0262,
  },
  // Mexico
  MX: {
    MARKETING: 2.9064,
    UTILITY: 0.6226,
    AUTHENTICATION: 0.6226,
    SERVICE: 0.6226,
  },
  // Morocco
  MA: {
    MARKETING: 3.0354,
    UTILITY: 1.6848,
    AUTHENTICATION: 1.6848,
    SERVICE: 1.6848,
  },
  // Nepal
  NP: {
    MARKETING: 5.3664,
    UTILITY: 0.2492,
    AUTHENTICATION: 0.2492,
    SERVICE: 0.2492,
  },
  // Netherlands
  NL: {
    MARKETING: 11.7078,
    UTILITY: 3.6656,
    AUTHENTICATION: 3.6656,
    SERVICE: 3.6656,
  },
  // Nigeria
  NG: {
    MARKETING: 3.7851,
    UTILITY: 0.4915,
    AUTHENTICATION: 0.4915,
    SERVICE: 0.4915,
  },
  // Oman
  OM: {
    MARKETING: 2.4993,
    UTILITY: 1.8091,
    AUTHENTICATION: 1.8091,
    SERVICE: 1.8091,
  },
  // Pakistan
  PK: { MARKETING: 3.4683, UTILITY: 1.1, AUTHENTICATION: 1.1, SERVICE: 1.1 },
  // Peru
  PE: {
    MARKETING: 5.1536,
    UTILITY: 2.1993,
    AUTHENTICATION: 2.1993,
    SERVICE: 2.1993,
  },
  // Poland
  PL: {
    MARKETING: 2.6754,
    UTILITY: 0.8918,
    AUTHENTICATION: 0.8918,
    SERVICE: 0.8918,
  },
  // Qatar
  QA: {
    MARKETING: 2.4993,
    UTILITY: 0.8789,
    AUTHENTICATION: 0.8789,
    SERVICE: 0.8789,
  },
  // Romania
  RO: {
    MARKETING: 6.2997,
    UTILITY: 2.1197,
    AUTHENTICATION: 2.1197,
    SERVICE: 2.1197,
  },
  // Russia
  RU: {
    MARKETING: 5.8767,
    UTILITY: 2.9311,
    AUTHENTICATION: 2.9311,
    SERVICE: 2.9311,
  },
  // Saudi Arabia
  SA: {
    MARKETING: 4.2114,
    UTILITY: 0.7834,
    AUTHENTICATION: 0.7834,
    SERVICE: 0.7834,
  },
  // Singapore
  SG: {
    MARKETING: 5.3664,
    UTILITY: 1.1727,
    AUTHENTICATION: 1.1727,
    SERVICE: 1.1727,
  },
  // South Africa
  ZA: {
    MARKETING: 2.7821,
    UTILITY: 0.6974,
    AUTHENTICATION: 0.6974,
    SERVICE: 0.6974,
  },
  // Spain
  ES: {
    MARKETING: 5.1801,
    UTILITY: 1.4648,
    AUTHENTICATION: 1.4648,
    SERVICE: 1.4648,
  },
  // Sri Lanka
  LK: {
    MARKETING: 5.3664,
    UTILITY: 0.1466,
    AUTHENTICATION: 0.1466,
    SERVICE: 0.1466,
  },
  // Turkey
  TR: {
    MARKETING: 0.7989,
    UTILITY: 0.066,
    AUTHENTICATION: 0.066,
    SERVICE: 0.066,
  },
  // Ukraine
  UA: {
    MARKETING: 6.2997,
    UTILITY: 2.1782,
    AUTHENTICATION: 2.1782,
    SERVICE: 2.1782,
  },
  // United Arab Emirates
  AE: {
    MARKETING: 4.2114,
    UTILITY: 1.1509,
    AUTHENTICATION: 1.1509,
    SERVICE: 1.1509,
  },
  // United Kingdom
  GB: {
    MARKETING: 4.6496,
    UTILITY: 1.6122,
    AUTHENTICATION: 1.6122,
    SERVICE: 1.6122,
  },
};

export const EXPLICIT_PRICING_COUNTRIES = Object.keys(EXPLICIT_PRICING);

/**
 * Meta's regional rate-card rows (INR per message). "Other" is the fallback
 * for countries not on the card at all.
 */
export const WHATSAPP_REGION_PRICING: Record<string, CategoryPricing> = {
  'North America': {
    MARKETING: 1.8313,
    UTILITY: 0.2491,
    AUTHENTICATION: 0.2491,
    SERVICE: 0.2491,
  },
  'Rest of Africa': {
    MARKETING: 1.6497,
    UTILITY: 0.293,
    AUTHENTICATION: 0.293,
    SERVICE: 0.293,
  },
  'Rest of Asia Pacific': {
    MARKETING: 6.1714,
    UTILITY: 0.8282,
    AUTHENTICATION: 0.8282,
    SERVICE: 0.8282,
  },
  'Rest of Central & Eastern Europe': {
    MARKETING: 6.2997,
    UTILITY: 1.5496,
    AUTHENTICATION: 1.5496,
    SERVICE: 1.5496,
  },
  'Rest of Latin America': {
    MARKETING: 5.4207,
    UTILITY: 0.8278,
    AUTHENTICATION: 0.8278,
    SERVICE: 0.8278,
  },
  'Rest of Middle East': {
    MARKETING: 2.8742,
    UTILITY: 0.6665,
    AUTHENTICATION: 0.6665,
    SERVICE: 0.6665,
  },
  'Rest of Western Europe': {
    MARKETING: 4.3378,
    UTILITY: 1.2516,
    AUTHENTICATION: 1.2516,
    SERVICE: 1.2516,
  },
  Other: {
    MARKETING: 4.4248,
    UTILITY: 0.5641,
    AUTHENTICATION: 0.5641,
    SERVICE: 0.5641,
  },
};

/**
 * Countries billed at their region's row (ISO-2), exactly as Meta's country
 * calling code table maps them; explicit markets are not listed here. Meta:
 * "If a country is not listed below, it maps to Other" — so "Other" names the
 * unlisted countries we have rows for (rate-card consumers such as the
 * marketing max-price caps look rows up strictly), and getPricingByCountry
 * falls back to the "Other" row for any country missing entirely.
 */
export const REGION_COUNTRIES: Record<string, string[]> = {
  'North America': [
    'CA', // Canada
    'US', // United States
  ],
  'Rest of Africa': [
    'DZ', // Algeria
    'AO', // Angola
    'BJ', // Benin
    'BW', // Botswana
    'BF', // Burkina Faso
    'BI', // Burundi
    'CM', // Cameroon
    'TD', // Chad
    'CG', // Republic of the Congo (Brazzaville)
    'ER', // Eritrea
    'ET', // Ethiopia
    'GA', // Gabon
    'GM', // Gambia
    'GH', // Ghana
    'GW', // Guinea-Bissau
    'CI', // Ivory Coast
    'KE', // Kenya
    'LS', // Lesotho
    'LR', // Liberia
    'LY', // Libya
    'MG', // Madagascar
    'MW', // Malawi
    'ML', // Mali
    'MR', // Mauritania
    'MZ', // Mozambique
    'NA', // Namibia
    'NE', // Niger
    'RW', // Rwanda
    'SN', // Senegal
    'SL', // Sierra Leone
    'SO', // Somalia
    'SS', // South Sudan
    'SD', // Sudan
    'SZ', // Swaziland
    'TZ', // Tanzania
    'TG', // Togo
    'TN', // Tunisia
    'UG', // Uganda
    'ZM', // Zambia
    'ZW', // Zimbabwe
  ],
  'Rest of Asia Pacific': [
    'AF', // Afghanistan
    'AU', // Australia
    'KH', // Cambodia
    'CN', // China
    'JP', // Japan
    'LA', // Laos
    'MN', // Mongolia
    'NZ', // New Zealand
    'PG', // Papua New Guinea
    'PH', // Philippines
    'TW', // Taiwan
    'TJ', // Tajikistan
    'TH', // Thailand
    'TM', // Turkmenistan
    'UZ', // Uzbekistan
    'VN', // Vietnam
  ],
  'Rest of Central & Eastern Europe': [
    'AL', // Albania
    'AM', // Armenia
    'AZ', // Azerbaijan
    'BY', // Belarus
    'BG', // Bulgaria
    'HR', // Croatia
    'CZ', // Czech Republic
    'GE', // Georgia
    'GR', // Greece
    'LV', // Latvia
    'LT', // Lithuania
    'MD', // Moldova
    'MK', // North Macedonia
    'RS', // Serbia
    'SK', // Slovakia
    'SI', // Slovenia
  ],
  'Rest of Latin America': [
    'BO', // Bolivia
    'CR', // Costa Rica
    'DO', // Dominican Republic
    'EC', // Ecuador
    'SV', // El Salvador
    'GT', // Guatemala
    'HT', // Haiti
    'HN', // Honduras
    'JM', // Jamaica
    'NI', // Nicaragua
    'PA', // Panama
    'PY', // Paraguay
    'PR', // Puerto Rico
    'UY', // Uruguay
    'VE', // Venezuela
  ],
  'Rest of Middle East': [
    'BH', // Bahrain
    'JO', // Jordan
    'LB', // Lebanon
    'YE', // Yemen
  ],
  'Rest of Western Europe': [
    'AT', // Austria
    'BE', // Belgium
    'DK', // Denmark
    'FI', // Finland
    'IE', // Ireland
    'NO', // Norway
    'PT', // Portugal
    'SE', // Sweden
    'CH', // Switzerland
  ],
  Other: [
    'AD', // Andorra
    'AG', // Antigua and Barbuda
    'BA', // Bosnia and Herzegovina
    'BB', // Barbados
    'BN', // Brunei
    'BS', // Bahamas
    'BT', // Bhutan
    'BZ', // Belize
    'CD', // DR Congo
    'CF', // Central African Republic
    'CU', // Cuba
    'CV', // Cape Verde
    'CY', // Cyprus
    'DJ', // Djibouti
    'DM', // Dominica
    'EE', // Estonia
    'FJ', // Fiji
    'FM', // Micronesia
    'GD', // Grenada
    'GN', // Guinea
    'GQ', // Equatorial Guinea
    'GY', // Guyana
    'IR', // Iran
    'IS', // Iceland
    'KI', // Kiribati
    'KM', // Comoros
    'KN', // Saint Kitts and Nevis
    'KP', // North Korea
    'KR', // South Korea
    'LC', // Saint Lucia
    'LI', // Liechtenstein
    'LU', // Luxembourg
    'MC', // Monaco
    'ME', // Montenegro
    'MH', // Marshall Islands
    'MM', // Myanmar
    'MO', // Macau
    'MT', // Malta
    'MU', // Mauritius
    'MV', // Maldives
    'NR', // Nauru
    'PS', // Palestine
    'PW', // Palau
    'SB', // Solomon Islands
    'SC', // Seychelles
    'SM', // San Marino
    'SR', // Suriname
    'ST', // Sao Tome and Principe
    'SY', // Syria
    'TL', // Timor-Leste
    'TO', // Tonga
    'TT', // Trinidad and Tobago
    'TV', // Tuvalu
    'VA', // Vatican City
    'VC', // Saint Vincent and the Grenadines
    'VU', // Vanuatu
    'WS', // Samoa
    'XK', // Kosovo
  ],
};

/**
 * Complete pricing by country ISO code (uppercase), INR per message: regional
 * rows expanded over their countries, explicit markets on top.
 */
export const WHATSAPP_PRICING: WhatsAppPricing = {
  ...Object.fromEntries(
    Object.entries(REGION_COUNTRIES).flatMap(([region, countries]) =>
      countries.map(iso => [iso, { ...WHATSAPP_REGION_PRICING[region] }]),
    ),
  ),
  ...EXPLICIT_PRICING,
};

/**
 * Authentication-international rate (INR per message) for the markets where
 * Meta publishes one; see pricing/authentication-international-rates.
 */
export const AUTH_INTERNATIONAL_PRICING: Record<string, number> = {
  EG: 4.7668,
  IN: 2.4971,
  ID: 9.9638,
  MY: 3.0639,
  NG: 5.5035,
  PK: 5.5035,
  SA: 4.3871,
  ZA: 1.4681,
  AE: 3.7342,
  BD: 7.036,
  IQ: 9.3749,
  KZ: 11.7216,
  KW: 8.789,
  MA: 5.9406,
  NP: 8.2087,
  OM: 4.3945,
  LK: 10.5541,
  UA: 5.4528,
};

/**
 * Default pricing for unknown/unmapped countries
 * Uses "Other" region pricing
 */
export const DEFAULT_PRICING: CategoryPricing = WHATSAPP_REGION_PRICING.Other;

/**
 * Get pricing for a country by ISO code
 * Returns default pricing if country not found
 *
 * @param isoCode - Uppercase ISO 2-letter country code (e.g., 'IN', 'US')
 * @returns CategoryPricing object with MARKETING, UTILITY, AUTHENTICATION, SERVICE prices
 */
export const getPricingByCountry = (isoCode: string): CategoryPricing => {
  const upperIso = isoCode.toUpperCase();
  return WHATSAPP_PRICING[upperIso] ?? DEFAULT_PRICING;
};

/**
 * Get price for a specific country and category
 *
 * @param isoCode - Uppercase ISO 2-letter country code
 * @param category - Billing category (MARKETING, UTILITY, AUTHENTICATION, SERVICE)
 * @returns Price per message in INR
 */
export const getPrice = (
  isoCode: string,
  category: BillingCategory,
): number => {
  const pricing = getPricingByCountry(isoCode);
  return pricing[category];
};

// ============================================
// RCS pricing
// ============================================

/**
 * Default flat RCS price per message (in INR), used as the final fallback when
 * neither a business-level RCS override nor an org-level RCS slab rate is set.
 *
 * RCS (v1) is billed as a single flat per-message rate with NO category and NO
 * per-country dimension — every RCS message is charged this rate (or the
 * configured business/org rate) regardless of recipient country. The pricing
 * config schema stays country-keyed (mirroring WhatsApp) so per-country RCS rates
 * can be added later without a data migration.
 */
export const RCS_DEFAULT_PRICE = 0.1;
